import { backendCommandsFactory } from './appImportBroker.js'
import type { IBroker } from './iterators/types.js'

interface LinkedInContactsPaging {
	total: number
	start: number
	count: number
}

interface LinkedInContactsPage {
	contacts: unknown[]
	paging: LinkedInContactsPaging
}

interface LinkedInConnectionsCount {
	count: number
}

async function isBackendImplementation(
	broker: IBroker,
	metadata?: object
): Promise<boolean> {
	const command =
		backendCommandsFactory.localStorage.createGetSnailyProfile(metadata)
	const snailyProfile = (await broker.publish(command)) as {
		isBackendAutomation: boolean
	}
	const result = snailyProfile.isBackendAutomation
	return result
}

async function getLinkedInHeaders(
	broker: IBroker,
	metadata?: object
): Promise<unknown> {
	const command =
		backendCommandsFactory.linkedInProxy.headers.createGetLastLinkedInRequestHeaders(
			metadata
		)
	const result = await broker.publish(command)
	return result
}

export async function getConnectionsCount(
	broker: IBroker,
	metadata?: object
): Promise<LinkedInConnectionsCount> {
	const useBackend = await isBackendImplementation(broker, metadata)
	let command
	if (useBackend) {
		command =
			backendCommandsFactory.apiProxy.backendAutomation.createGetLinkedInConnectionsCount(
				metadata
			)
	} else {
		const headers = await getLinkedInHeaders(broker, metadata)
		const profileCommand =
			backendCommandsFactory.localStorage.createGetLinkedInProfile(metadata)
		const linkedInProfile = (await broker.publish(profileCommand)) as {
			publicIdentifier?: string
		}
		const publicIdentifier = linkedInProfile.publicIdentifier ?? ''
		command =
			backendCommandsFactory.linkedInProxy.authorize.V2.createGetConnectionsCount(
				publicIdentifier,
				headers,
				metadata
			)
	}
	const result = (await broker.publish(command)) as LinkedInConnectionsCount
	return result
}

export async function getContactsFromMyConnections(
	broker: IBroker,
	start: number,
	count: number,
	metadata?: object
): Promise<LinkedInContactsPage> {
	const useBackend = await isBackendImplementation(broker, metadata)
	let command
	if (useBackend) {
		command =
			backendCommandsFactory.apiProxy.backendAutomation.createGetLinkedInContactsFromMyConnections(
				start,
				count,
				metadata
			)
	} else {
		command =
			backendCommandsFactory.linkedInService.createGetContactsFromConnections(
				start,
				count,
				metadata
			)
	}
	const result = (await broker.publish(command)) as LinkedInContactsPage
	return result
}

export async function getContactsFromSearchPage(
	broker: IBroker,
	searchRequest: { parameters: Record<string, string>; start: number },
	referrer: string,
	metadata?: object
): Promise<LinkedInContactsPage> {
	const useBackend = await isBackendImplementation(broker, metadata)
	let command
	if (useBackend) {
		command =
			backendCommandsFactory.apiProxy.backendAutomation.createGetLinkedInContactsFromSearchPage(
				searchRequest,
				referrer,
				metadata
			)
	} else {
		command =
			backendCommandsFactory.linkedInService.createGetContactsFromSearchPage(
				searchRequest.parameters,
				searchRequest.start,
				referrer,
				metadata
			)
	}
	const result = (await broker.publish(command)) as LinkedInContactsPage
	return result
}

export async function getContactsFromGroupPage(
	broker: IBroker,
	searchRequest: { groupId: string; start: number; pageSize: number },
	typeaheadQuery: string,
	metadata?: object
): Promise<LinkedInContactsPage> {
	const useBackend = await isBackendImplementation(broker, metadata)
	let command
	if (useBackend) {
		command =
			backendCommandsFactory.apiProxy.backendAutomation.createGetLinkedInContactsFromGroupPage(
				searchRequest,
				typeaheadQuery,
				metadata
			)
	} else {
		command =
			backendCommandsFactory.linkedInService.createGetContactsFromGroupPage(
				searchRequest.groupId,
				searchRequest.start,
				searchRequest.pageSize,
				typeaheadQuery,
				metadata
			)
	}
	const result = (await broker.publish(command)) as LinkedInContactsPage
	return result
}

export async function getContactsFromSalesNavigatorSearch(
	broker: IBroker,
	searchRequest: object,
	referrer: string,
	metadata?: object
): Promise<LinkedInContactsPage> {
	const useBackend = await isBackendImplementation(broker, metadata)
	let command
	if (useBackend) {
		command =
			backendCommandsFactory.apiProxy.backendAutomation.createGetSalesNavigatorContactsFromSearchPage(
				searchRequest,
				referrer,
				metadata
			)
	} else {
		command =
			backendCommandsFactory.salesNavigatorService.createGetContactsFromSearchPage(
				searchRequest,
				referrer,
				metadata
			)
	}
	const result = (await broker.publish(command)) as LinkedInContactsPage
	return result
}

export async function getContactsFromSalesNavigatorLeadsList(
	broker: IBroker,
	searchRequest: object,
	referrer: string,
	metadata?: object
): Promise<LinkedInContactsPage> {
	const useBackend = await isBackendImplementation(broker, metadata)
	let command
	if (useBackend) {
		command =
			backendCommandsFactory.apiProxy.backendAutomation.createGetSalesNavigatorContactsFromLeadsList(
				searchRequest,
				referrer,
				metadata
			)
	} else {
		command =
			backendCommandsFactory.salesNavigatorService.createGetContactsFromLeadsList(
				searchRequest,
				referrer,
				metadata
			)
	}
	const result = (await broker.publish(command)) as LinkedInContactsPage
	return result
}
